import { useEffect, useMemo, useRef, useState } from "react"

import { PAGE_NUMBER } from "@/helpers/filters.helper"
import { useFilters } from "@/hooks/useFilters"
import { LIST_MENU_DROPDOWN_SMART_SEARCH_ID } from "../const/classes.constants"
import {
  RANGE_DELIMITER,
  RANGE_DELIMITER_QUERY_STRING
} from "../const/date.constants"
import { COUNT_UNDO_VALUES_FOR_FILTER_INPUT } from "../const/limit.constants"
import {
  APPLY_FILTER_SYMBOL,
  DATA_ID_SMART_SEARCH,
  MENU_DROPDOWN_SMART_SEARCH_ID
} from "../const/symbol.constants"
import { getTimeDisplayFormatFromString } from "../helpers/date.helpers"
import { FilterService } from "../service/FilterService"
import { StringService } from "../service/StringService"

import type { FormEvent } from "react"

export const isKeyboardNavigation = ({ key, shiftKey }: React.KeyboardEvent) =>
  !shiftKey && (key === "ArrowLeft" || key === "ArrowRight")

export const isUndo = ({ key, metaKey }: React.KeyboardEvent) =>
  key === "z" && metaKey

export const isPaste = ({ key }: React.KeyboardEvent) => key === "v"

export const extractInnerText = ({
  currentTarget: { innerText }
}: FormEvent<HTMLDivElement>) => (innerText === "\n" ? "" : innerText)

export const useSearch = ({
  data,
  filterOptions,
  onlySearch,
  prefix = ""
}: any) => {
  const ref = useRef<HTMLDivElement>(null)
  const syncFilterOptions = useRef<boolean>(false)
  const lastDataRef = useRef<string>("")
  const { searchParams, setSearchParams, searchParamsProps, useQueryString } =
    useFilters()
  const [open, setOpen] = useState<boolean>(false)
  const [trigger, setTrigger] = useState<Record<string, any>>({})
  const rerender = (value = {}) => setTrigger(value)
  const previousText = useRef<{ text: string; caretPos: number }[]>([])
  const lastActiveToken = useRef<string>("")

  const FilterServiceInstance = useRef<FilterService>(
    new FilterService(filterOptions)
  ).current
  const StringServiceInstance = useMemo<StringService>(
    () => new StringService(ref, FilterServiceInstance, { initialText: "" }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const onClick = (e: any) => {
    const { key, domEvent, replaceByIndex } = e

    if (replaceByIndex !== undefined) return rerender()

    if (domEvent) {
      domEvent?.stopPropagation()
      domEvent?.preventDefault()
    }

    const indexForClear =
      e.target?.getAttribute("data-index-clear-filter-smart-search") ?? false

    const rect = e.target?.getBoundingClientRect()
    const clickX = !!rect && e.clientX - rect.left

    if (indexForClear !== false && clickX && clickX >= rect.width - 30) {
      StringServiceInstance.removeTokenByIndex(indexForClear)

      const tokens = StringServiceInstance.getTokens()

      if (tokens[indexForClear]?.isEmpty) {
        StringServiceInstance.removeTokenByIndex(indexForClear)
      }

      StringServiceInstance.setText(
        StringServiceInstance.buildTextFromTokens()
      ).tokenizer()
      StringServiceInstance.setCursorAtTheEnd()
      StringServiceInstance.updateCaretPosAndData()

      updateSearchParamsByTokens()
      rerender()

      return
    }

    if (!key) {
      StringServiceInstance.updateCaretPosAndData()

      if (!document.getSelection()?.toString()) {
        rerender()
      }

      return
    }

    StringServiceInstance.setText(
      StringServiceInstance.buildTextFromTokens(
        key,
        StringServiceInstance.getCaretPos()
      )
    ).tokenizer(StringServiceInstance.getCaretPos())

    const filter = FilterServiceInstance.getFilterByLabel(key)

    if (filter?.valuesInQuotes) {
      const index = StringServiceInstance.updateValueByTokenIndex('""')

      StringServiceInstance.setText(StringServiceInstance.buildTextFromTokens())
        .tokenizer()
        .updateCaretPosAndData(
          StringServiceInstance.getTokens()[index]?.endCaretPos - 1
        )
    }

    rerender()
  }

  const onInput = (event: FormEvent<HTMLDivElement>) => {
    try {
      let text = extractInnerText(event)
      const nativeEvent: any = event?.nativeEvent
      const data = nativeEvent?.data
      const isValidStringWithQuote =
        StringServiceInstance.isValidStringsWithQuote(text)
      let nextCaretPos

      if (data === '"' && !isValidStringWithQuote) {
        const caretPos = StringServiceInstance.getCaretPos()
        text = text.slice(0, caretPos) + '"' + text.slice(caretPos)
      }

      const isDelimiter = data === ":"

      if ((data === "," || isDelimiter) && isValidStringWithQuote) {
        const caretPos = StringServiceInstance.getCaretPos()
        const currentToken = StringServiceInstance.getTokenByCaretPos()
        const { id, delimiter, value, endCaretPos } = currentToken || {}
        const isQuotedValue = /^".*"$/.test(value || "")
        const useValuesInQuotes = FilterServiceInstance.getFilterByLabel(
          id?.toLowerCase()
        )?.valuesInQuotes
        const valuesInQuotes = isQuotedValue && useValuesInQuotes
        let offset = 1

        const getQuotedValue = (text: string) => ({
          text: text + '""',
          offset: 2
        })

        if (valuesInQuotes) {
          if (caretPos === endCaretPos) {
            const values = getQuotedValue(text)

            text = values.text
            offset = values.offset
          } else {
            text = text.replace(/"([^"]*)"/g, (_, content) => {
              if (!content.endsWith(",")) return `"${content}"`
              if (offset !== 3) offset = 3

              const commas = (content.match(/,/g) || []).length
              const cleanContent = content.replace(/,/g, "")

              return `"${cleanContent}"` + ',""'.repeat(commas)
            })
          }

          nextCaretPos = caretPos + offset
        } else if (useValuesInQuotes && isDelimiter && !value && !delimiter) {
          const values = getQuotedValue(text.trim())

          text = values.text
          offset = values.offset
          nextCaretPos = caretPos + offset
        }
      }

      StringServiceInstance.setText(text).tokenizer()
      StringServiceInstance.tokenizer().updateCaretPosAndData(nextCaretPos)
      rerender()
    } catch {
      return
    }
  }

  const onClear = (
    _: unknown,
    options?: { apply?: boolean; focus?: boolean }
  ) => {
    const { apply, focus = true } = options || {}
    const fn = apply ? searchParamsProps.updateSearchParams : setSearchParams

    onInput({
      currentTarget: {
        innerText: ""
      }
    } as any)
    queueMicrotask(() => {
      fn(() => new URLSearchParams({}))
      if (focus) ref.current?.focus()
    })
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    try {
      const { key } = e

      e.stopPropagation?.()

      if (isUndo(e)) {
        e.preventDefault()

        previousText.current.pop()

        const prev = previousText.current[previousText.current.length - 1]

        if (prev) {
          StringServiceInstance.setText(prev.text ?? "").tokenizer(
            prev.caretPos
          )

          rerender()
        }

        return
      }

      if (key === "ArrowDown") {
        e.preventDefault()

        document
          .getElementById(MENU_DROPDOWN_SMART_SEARCH_ID)
          ?.querySelector<HTMLElement>(
            `[${DATA_ID_SMART_SEARCH}="${LIST_MENU_DROPDOWN_SMART_SEARCH_ID}"]`
          )
          ?.focus()
        ref.current?.blur()
      }

      if (key === "Enter") {
        e.stopPropagation?.()
        e.preventDefault?.()

        const { detail } = e
        const currentToken = StringServiceInstance.getTokenByCaretPos()

        if (
          currentToken?.isEmpty === false &&
          !onlySearch &&
          detail !== APPLY_FILTER_SYMBOL
        ) {
          const tokens = StringServiceInstance.getTokens()
          const currentTokenIndex = tokens.findIndex(
            (token) => token.id === currentToken.id
          )
          const nextToken = tokens[currentTokenIndex + 1]

          if (nextToken?.isEmpty) {
            StringServiceInstance.setCursorPosition(
              nextToken.endCaretPos
            ).tokenizer()
          } else {
            //nokodemcode
            if (ref.current) ref.current.innerHTML = ref.current.innerHTML + " "

            onInput({
              currentTarget: {
                innerText: StringServiceInstance.getText() + " "
              }
            } as any)

            queueMicrotask(() => {
              const currentToken = tokens[currentTokenIndex]

              StringServiceInstance.setCursorPosition(
                currentToken.endCaretPos + 1
              ).tokenizer(currentToken.endCaretPos + 1)

              rerender()
              ref.current?.focus()
            })
          }

          rerender()
          ref.current?.focus()

          return
        }

        const tokensWithValues = StringServiceInstance.getNotEmptyTokens()

        if (tokensWithValues.some(({ error }) => !!error) && ref.current) {
          Array.from(
            ref.current.querySelectorAll(
              ".value-smart-search, .label-smart-search"
            )
          ).forEach((el) => {
            el.classList.remove("value-smart-search")
            el.classList.remove("edit-smart-search")
          })

          return
        }

        const params = updateSearchParamsByTokens(
          searchParamsProps.updateSearchParams,
          tokensWithValues
        )

        if (!useQueryString && params) {
          StringServiceInstance.setText(
            FilterServiceInstance.syncFiltersValueWithSearchParams(
              params
            ).getTextInSmartSearchFormat(params)
          ).tokenizer()

          rerender()
        }

        onBlur()
        ref.current?.blur()

        return
      }

      if (key === "Tab") e.preventDefault()
    } catch {
      return
    }
  }

  const onKeyUp = (e: React.KeyboardEvent) => {
    try {
      if (isKeyboardNavigation(e) || isPaste(e)) {
        StringServiceInstance.tokenizer().updateCaretPosAndData(
          StringServiceInstance.getCursorPosition()
        )

        rerender()

        return
      }
    } catch {
      return
    }
  }

  const onFocus = () => {
    setOpen(true)
  }

  const onBlur = () => {
    setOpen(false)
  }

  const onPaste = (e: React.ClipboardEvent) => {
    const token = StringServiceInstance.getTokenByCaretPos()
    const id = token?.id

    if (!id) return

    const value = token?.value

    if (value?.startsWith('"') && value?.endsWith('"')) return

    const filter = FilterServiceInstance.getFilterByLabel(id)

    if (!filter?.trimValuesOnPaste) return

    const selection = window.getSelection()

    if (!selection?.rangeCount) return

    const originalText = (
      e.clipboardData || (window as any).clipboardData
    ).getData("text/plain")
    const noSpacesText = StringServiceInstance.transformQuotedSpaces(
      originalText,
      "mask"
    )

    const text = StringServiceInstance.transformQuotedSpaces(
      noSpacesText
        .split(/[\s,]+/g)
        .filter(Boolean)
        .join(StringServiceInstance.tokenDelimiter),
      "unmask"
    )

    if (originalText === text) return

    e.preventDefault()

    const range = selection.getRangeAt(0)

    range.deleteContents()

    const node = document.createTextNode(text)

    range.insertNode(node)
    selection.collapse(node, node.length)

    onInput({
      currentTarget: e.currentTarget,
      nativeEvent: { data: text }
    } as unknown as FormEvent<HTMLDivElement>)
  }

  const updateSearchParamsByTokens = (
    fn: typeof setSearchParams = setSearchParams,
    tokensWithValues = StringServiceInstance.getNotEmptyTokens()
  ) => {
    let result

    fn((prev) => {
      const params = new URLSearchParams(prev)

      params.set(`${prefix}${PAGE_NUMBER}`, "0")

      const filters = FilterServiceInstance.getFilterInOrder(
        tokensWithValues.map(({ id, delimiter, value }) => {
          if (id && !delimiter && !value) return "query"

          return id
        })
      )

      const valuesByGroupKey = filters.reduce(
        (acc, filter) => {
          if (filter.groupKey) acc[filter.groupKey] = ""

          return acc
        },
        {} as Record<string, string>
      )

      filters.forEach((filter, index) => {
        const { type } = filter
        let value = tokensWithValues[index]?.value

        if (type === "query") {
          const indexOfValue = tokensWithValues.findIndex(
            ({ id, delimiter, value }) => id && !delimiter && !value
          )

          value = tokensWithValues[indexOfValue]?.id
        }

        if (type === "time-range") {
          value = getTimeDisplayFormatFromString(value, "timestamp")
            ?.split(RANGE_DELIMITER)
            .map((v) => v.trim())
            .join(RANGE_DELIMITER_QUERY_STRING)
        }

        if (type === "single") {
          value = filter.options?.find(({ label }) => label === value)?.value
        }

        if (type === "multiple" && value) {
          value = value
            .split(",")
            .map(
              (v: string) =>
                filter.options?.find(({ label }) => label === v)?.value
            )
            .filter(Boolean)
            .join(",")
        }

        if (value?.[value.length - 1] === ",") value = value.slice(0, -1)

        if (value) {
          const wrappedInQuotationMarks = /^".*"$/im.test(value || "")

          if (wrappedInQuotationMarks)
            value = value.substring(1, value.length - 1)

          const currentKey = filter.groupKey || filter.key

          if (valuesByGroupKey[currentKey] !== undefined) {
            const currentValueByKey = valuesByGroupKey[currentKey]

            valuesByGroupKey[currentKey] =
              `${currentValueByKey}${currentValueByKey ? "," : ""}${value}`

            if (filter.groupKey) params.delete(filter.key)
          }

          params.set(currentKey, valuesByGroupKey[currentKey] || value)
        } else {
          if (valuesByGroupKey[filter.key]) return

          params.delete(filter.key)
        }
      })

      if (Object.keys(data).length > 0) result = params

      return params
    })

    return result
  }

  const syncFiltersWithUrlParams = () => {
    StringServiceInstance.setText(
      FilterServiceInstance.syncFiltersValueWithSearchParams(
        searchParams
      ).getTextInSmartSearchFormat(searchParams)
    ).tokenizer()
    rerender()
  }

  const updateTokenValue = (value?: string) => {
    const index = StringServiceInstance.updateValueByTokenIndex(value)

    StringServiceInstance.setText(StringServiceInstance.buildTextFromTokens())
      .tokenizer()
      .updateCaretPosAndData(
        StringServiceInstance.getTokens()[index]?.endCaretPos
      )

    rerender()
  }

  useEffect(() => {
    if (Object.keys(data).length === 0) return

    // Only update filter options if data has actually changed (deep comparison)
    const dataString = JSON.stringify(data)
    if (lastDataRef.current !== dataString) {
      lastDataRef.current = dataString
      FilterServiceInstance.updateFiltersOptions(data)
      rerender()
    }

    // Only set text from searchParams if user is not actively using the input
    if (!syncFilterOptions.current && !open) {
      syncFiltersWithUrlParams()
      syncFilterOptions.current = true
    }
  }, [data])

  useEffect(() => {
    if (open) return
    syncFiltersWithUrlParams()
  }, [searchParams])

  useEffect(() => {
    if (!open || ref.current === null) return

    ref.current.focus()
    StringServiceInstance.setCursorPosition()

    const prev = previousText.current[previousText.current.length - 1]
    const text = StringServiceInstance.getText()

    const currentToken = StringServiceInstance.getTokenByCaretPos()

    if (currentToken?.isEmpty === false) updateSearchParamsByTokens()

    const { id, value } = currentToken || {}
    const isMultiple =
      FilterServiceInstance.getFilterByLabel(id || "")?.type === "multiple"
    const notCommaAtEnd = value && value?.[value.length - 1] !== ","
    const notSameToken = id !== lastActiveToken.current

    if (isMultiple && notSameToken && notCommaAtEnd) {
      updateTokenValue(`${currentToken.value},`)
    }

    lastActiveToken.current = id

    if (prev?.text === text) return

    previousText.current.push({
      text: StringServiceInstance.getText(),
      caretPos: StringServiceInstance.getCursorPosition()
    })

    if (previousText.current.length > COUNT_UNDO_VALUES_FOR_FILTER_INPUT) {
      previousText.current.shift()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  return {
    html: StringServiceInstance.buildHtmlFromTokens(),
    showPlaceholder: StringServiceInstance.getNotEmptyTokens().length === 0,
    dirty: searchParamsProps.isDirty,
    open,
    refInput: ref,
    handlers: {
      onClick,
      onInput,
      onKeyDown,
      onKeyUp,
      onFocus,
      onBlur,
      onClear,
      onPaste,
      updateTokenValue
    },
    services: {
      FilterService: FilterServiceInstance,
      StringService: StringServiceInstance
    }
  }
}
